import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, CircularProgress } from '@mui/material';
import CustomSnackbar from '../components/Message.Component';
import { useGetShippingCostsList } from '../../client/hooks/shipping/shipping.queries';
import { 
    useProcessShippingCostsBatch, 
    useStartShippingCostsExport, 
    useDownloadShippingCostsExportFile,
    useImportShippingCostsFromML 
} from '../../client/hooks/shipping/shipping.mutations';
import { useCheckShippingCostsExportStatus } from '../../client/hooks/shipping/shipping.queries';
import FreteGratisHeader from './components/frete-gratis/FreteGratisHeader';
import FreteGratisFilters from './components/frete-gratis/FreteGratisFilters';
import FreteGratisTable from './components/frete-gratis/FreteGratisTable';
import FreteGratisPagination from './components/frete-gratis/FreteGratisPagination';
import FreteGratisImportModal from './components/frete-gratis/FreteGratisImportModal';
import FreteGratisExportModal from './components/frete-gratis/FreteGratisExportModal';

const FreteGratisPage = () => {
    const [alert, setAlert] = useState(false);
    const [severity, setSeverity] = useState('success');
    const [message, setMessage] = useState('');
    
    // Estados para importação e exportação
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const [exportStatus, setExportStatus] = useState('idle');
    const [currentJobId, setCurrentJobId] = useState(null);
    const pollingRef = useRef(null);
    
    const [filters, setFilters] = useState({
        item_mlb: '',
        list_cost_min: '',
        list_cost_max: '',
        createdAt_start: '',
        createdAt_end: '',
        user_id: ''
    });
    
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20
    });

    const buildQueryParams = useCallback(() => {
        const params = {
            page: pagination.page,
            limit: pagination.limit
        };

        if (filters.item_mlb) {
            params.item_mlb = filters.item_mlb;
        }

        if (filters.list_cost_min) {
            params.list_cost_min = filters.list_cost_min;
        }

        if (filters.list_cost_max) {
            params.list_cost_max = filters.list_cost_max;
        }

        if (filters.createdAt_start) {
            params.createdAt_start = filters.createdAt_start;
        }

        if (filters.createdAt_end) {
            params.createdAt_end = filters.createdAt_end;
        }

        if (filters.user_id) {
            params.user_id = filters.user_id;
        }

        return params;
    }, [filters, pagination]);

    const queryParams = buildQueryParams();
    
    const {
        data: response,
        isLoading,
        isError,
        error,
        refetch
    } = useGetShippingCostsList(queryParams, true);

    // Hooks para operações
    const processBatchMutation = useProcessShippingCostsBatch();
    const startExportMutation = useStartShippingCostsExport();
    const downloadMutation = useDownloadShippingCostsExportFile();
    const importFromMLMutation = useImportShippingCostsFromML();
    const checkStatusMutation = useCheckShippingCostsExportStatus();
    
    // Estado para controlar se já fez download (evitar múltiplos downloads)
    const [downloadCompleted, setDownloadCompleted] = useState(false);

    const data = response?.data || [];
    const paginationInfo = response?.pagination;

    useEffect(() => {
        if (isError) {
            showSnackbar('Erro ao carregar dados de frete', 'error');
        }
    }, [isError, error]);

    const showSnackbar = (msg, sev = 'success') => {
        setMessage(msg);
        setSeverity(sev);
        setAlert(true);
    };

    const handleFiltersChange = useCallback((newFilters) => {
        setFilters(newFilters);
        setPagination(prev => ({ ...prev, page: 1 }));
    }, []);

    const handleClearFilters = useCallback(() => {
        setFilters({
            item_mlb: '',
            list_cost_min: '',
            list_cost_max: '',
            createdAt_start: '',
            createdAt_end: '',
            user_id: ''
        });
        setPagination(prev => ({ ...prev, page: 1 }));
    }, []);

    const handlePageChange = useCallback((newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    }, []);

    const handleRowsPerPageChange = useCallback((newRowsPerPage) => {
        setPagination({
            page: 1,
            limit: newRowsPerPage
        });
    }, []);

    const handleRefresh = useCallback(() => {
        refetch();
        showSnackbar('Dados atualizados com sucesso!', 'success');
    }, [refetch]);

    // Função de polling manual para verificar status
    const startPolling = useCallback((jobId) => {
        clearPolling();
        
        let hasDownloaded = false; // Flag local para evitar múltiplos downloads
        
        pollingRef.current = setInterval(async () => {
            try {
                const statusResponse = await checkStatusMutation.mutateAsync({ jobId });
                
                if (statusResponse.status === 'completed' && !hasDownloaded) {
                    clearPolling();
                    hasDownloaded = true; // Marcar como downloading localmente
                    setExportStatus('downloading');
                    
                    // Download do arquivo
                    try {
                        const fileBlob = await downloadMutation.mutateAsync(jobId);
                        
                        const url = window.URL.createObjectURL(fileBlob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `frete-gratis-${new Date().toISOString().split('T')[0]}.xls`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(url);
                        
                        setExportStatus('idle');
                        setCurrentJobId(null);
                        setDownloadCompleted(true);
                        showSnackbar('Arquivo exportado com sucesso!', 'success');
                    } catch (downloadError) {
                        setExportStatus('idle');
                        setCurrentJobId(null);
                        setDownloadCompleted(true);
                        showSnackbar('Erro ao fazer download do arquivo', 'error');
                    }
                    
                } else if (statusResponse.status === 'failed') {
                    clearPolling();
                    setExportStatus('idle');
                    setCurrentJobId(null);
                    setDownloadCompleted(true);
                    showSnackbar('Falha na exportação', 'error');
                }
                
            } catch (error) {
                clearPolling();
                setExportStatus('idle');
                setCurrentJobId(null);
                setDownloadCompleted(true);
                showSnackbar('Erro ao verificar status da exportação', 'error');
            }
        }, 3000); // Verifica a cada 3 segundos
    }, [checkStatusMutation, downloadMutation]);

    const handleExportClick = useCallback(() => {
        if (exportStatus !== 'idle') return;
        setExportModalOpen(true);
    }, [exportStatus]);

    const handleExportConfirm = useCallback(({ weeklyComparison }) => {
        if (exportStatus !== 'idle') return;
        
        setExportStatus('processing');
        setDownloadCompleted(false);
        
        const exportData = weeklyComparison ? { weeklyComparison: true } : {};
        
        startExportMutation.mutate(exportData, {
            onSuccess: (response) => {
                if (response.jobId) {
                    setCurrentJobId(response.jobId);
                    showSnackbar('Exportação iniciada. Aguarde o processamento...', 'info');
                    startPolling(response.jobId);
                } else {
                    setExportStatus('idle');
                    setDownloadCompleted(false);
                    showSnackbar('Erro: JobId não retornado', 'error');
                }
            },
            onError: () => {
                setExportStatus('idle');
                setDownloadCompleted(false);
                showSnackbar('Erro ao iniciar exportação', 'error');
            }
        });
    }, [exportStatus, startExportMutation, startPolling]);

    // Função para limpar polling
    const clearPolling = () => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    };

    // Removido o effect que causava múltiplos downloads

    // Cleanup no unmount
    useEffect(() => {
        return () => clearPolling();
    }, []);

    const handleImport = useCallback(() => {
        setImportModalOpen(true);
    }, []);

    const handleImportFromML = useCallback(() => {
        importFromMLMutation.mutate(undefined, {
            onSuccess: () => {
                showSnackbar('Importação do Mercado Livre iniciada com sucesso!', 'success');
                refetch();
            },
            onError: () => {
                showSnackbar('Erro ao importar do Mercado Livre', 'error');
            }
        });
    }, [importFromMLMutation, refetch]);

    const handleImportConfirm = useCallback((data) => {
        processBatchMutation.mutate(data, {
            onSuccess: () => {
                showSnackbar(`${data.mlbs.length} MLBs importados com sucesso!`, 'success');
                refetch();
            },
            onError: () => {
                showSnackbar('Erro ao processar importação', 'error');
            }
        });
    }, [processBatchMutation, refetch]);

    return (
        <React.Fragment>
            <CustomSnackbar
                open={alert}
                onClose={() => setAlert(false)}
                severity={severity}
                message={message}
            />

            <Box sx={{
                paddingTop: { xs: 1.5, sm: 2, md: 3 },
                paddingRight: { xs: 1.5, sm: 2, md: 3 },
                paddingLeft: { xs: 1.5, sm: 2, md: 3 },
                height: 'auto',
                bgcolor: '#fafafa',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
                pb: 3
            }}>
                <FreteGratisHeader
                    totalCount={paginationInfo?.total}
                    loading={isLoading}
                    onRefresh={handleRefresh}
                    onExport={handleExportClick}
                    onImport={handleImport}
                    onImportFromML={handleImportFromML}
                    exportStatus={exportStatus}
                    importFromMLLoading={importFromMLMutation.isPending}
                />

                <FreteGratisFilters
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    onClearFilters={handleClearFilters}
                />

                {paginationInfo && (
                    <FreteGratisPagination
                        pagination={paginationInfo}
                        onPageChange={handlePageChange}
                        onRowsPerPageChange={handleRowsPerPageChange}
                    />
                )}

                <Box sx={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', flex: 1 }}>
                    {isLoading ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            my: 8,
                            height: 200
                        }}>
                            <CircularProgress size={50} sx={{ color: "#ff9800" }} />
                        </Box>
                    ) : (
                        <FreteGratisTable
                            data={data}
                            loading={isLoading}
                        />
                    )}
                </Box>
            </Box>

            <FreteGratisImportModal
                open={importModalOpen}
                onClose={() => setImportModalOpen(false)}
                onConfirm={handleImportConfirm}
            />

            <FreteGratisExportModal
                open={exportModalOpen}
                onClose={() => setExportModalOpen(false)}
                onConfirm={handleExportConfirm}
                loading={exportStatus === 'processing'}
            />
        </React.Fragment>
    );
};

export default FreteGratisPage;